import { useNavigate } from 'react-router-dom'
import { useQueryClient } from '@tanstack/react-query'
import type { UUID } from 'crypto'
import { User } from '@/models/Users'
import { useUpdateUserMutation } from '@/utils/hooks/users'
import { loadProgress } from '../api/onboardingProgressAPI'

// Bounds every awaited step of the switch so a hang (not just a rejection)
// can't stall the sequence indefinitely — a hung invalidateQueries or
// loadProgress would otherwise leave the user on "Switching…" forever, never
// even reaching the fail-open path below.
const SWITCH_STEP_TIMEOUT_MS = 8000

function withTimeout<T>(promise: Promise<T>, ms: number): Promise<T> {
  return new Promise((resolve, reject) => {
    const timer = setTimeout(() => reject(new Error(`timed out after ${ms}ms`)), ms)
    promise.then(
      (value) => {
        clearTimeout(timer)
        resolve(value)
      },
      (err) => {
        clearTimeout(timer)
        reject(err)
      },
    )
  })
}

/**
 * Switches the caller's selected org and navigates to it, matching the
 * established behaviour in SelectOrg.tsx's org switcher: await the
 * currentUser cache refresh before navigating (so the shell/GuardedOutlet
 * read the new selected_org_uuid, not a stale one), then route to
 * /onboarding if the target org's own onboarding isn't complete, else /.
 * Shared by OrgDetails.tsx and OrgNumber.tsx's "Go to org" action so the two
 * don't drift into their own, differently-broken versions of this sequence.
 */
export function useSwitchToOrg() {
  const updateUserMutation = useUpdateUserMutation()
  const queryClient = useQueryClient()
  const navigate = useNavigate()

  return async (token: string, user: User, targetOrgUuid: string) => {
    // Bounded but NOT fail-open: a hang here must surface as a rejection to
    // the caller (same as any other mutateAsync failure), not be swallowed —
    // unlike the steps below, we don't yet know whether the switch itself
    // succeeded, so silently proceeding to navigate would be presenting a
    // failed switch as a successful one.
    await withTimeout(
      updateUserMutation.mutateAsync({
        token,
        editedUser: { ...user, selected_org_uuid: targetOrgUuid as UUID },
      }),
      SWITCH_STEP_TIMEOUT_MS,
    )

    // onboarding_exited is a global flag meaning "I chose to skip onboarding",
    // but that decision belongs to the org it was made for. Left stale after
    // a switch, it can suppress GuardedOutlet's mid-onboarding redirect for
    // the new org (MainLayout.tsx:34) and strand the user on the wrong page —
    // most reachable via the loadProgress fail-open below. The switch itself
    // just succeeded (above), so this is a clean point to drop a decision
    // that no longer applies.
    localStorage.removeItem('onboarding_exited')

    // Falls back to the main app, not onboarding, when progress can't be
    // determined (rejection or timeout). The two failure modes are not
    // symmetric: failing toward the main app lands on a first-class recovery
    // affordance (BLOCKED_PREFIXES still gates sensitive routes, and "Resume
    // Onboarding" is always present in the nav); failing toward onboarding
    // would hard-reload a completed user into a wizard whose own bounce-out
    // depends on the same loadProgress call that just failed.
    let needsOnboarding = false
    try {
      // useUpdateUserMutation's own onSuccess fires this same invalidation
      // but doesn't return the promise, so React Query never waits for it —
      // awaiting it explicitly here is what actually guarantees the refetch
      // completes before we navigate.
      await withTimeout(
        queryClient.invalidateQueries({ queryKey: ['currentUser'] }),
        SWITCH_STEP_TIMEOUT_MS,
      )

      const progress = await withTimeout(
        loadProgress(token, targetOrgUuid),
        SWITCH_STEP_TIMEOUT_MS,
      )
      needsOnboarding = !!progress && !progress.is_complete
    } catch (err) {
      console.error(
        '[onboarding] useSwitchToOrg: switch sequence failed after the org changed server-side, defaulting to main app',
        err,
      )
      needsOnboarding = false
    }

    if (needsOnboarding) {
      // Hard reload, not navigate(): every current caller of this hook already
      // lives on /onboarding, so navigate('/onboarding') is a same-route no-op.
      // OnboardingV2Provider only reads `initialState` through useState's lazy
      // initializer, and OnboardingShell deliberately never remounts it once
      // hydrated (to survive background refetches mid-session) — so a SPA nav
      // here would leave the PREVIOUS org's step/wizard state mounted while
      // autosaves keep silently writing to the wrong org's progress record.
      // A full reload forces a genuinely fresh mount for the new org instead.
      window.location.assign('/onboarding')
      return
    }

    navigate('/')

    // Background refresh for dependent queries — same as SelectOrg.tsx.
    queryClient.invalidateQueries({ queryKey: ['fundingNeeds'] })
    queryClient.invalidateQueries({ queryKey: ['userOrgs'] })
    queryClient.invalidateQueries({ queryKey: ['currentOrgs'] })
  }
}
